import { useEffect, useRef, useState } from 'react';

/**
 * Shared motion primitives for the demo polish layer. Every JS-driven animation
 * (typewriter, count-up, run/scan sequences, toasts) gates on
 * {@link usePrefersReducedMotion} so that under `prefers-reduced-motion: reduce`
 * the UI renders its final state instantly. CSS-driven motion uses Tailwind's
 * `motion-reduce:` variants instead; this hook is only for the JS paths.
 */

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

/** True when the OS/browser requests reduced motion; re-reads on preference change. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(REDUCE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(REDUCE_QUERY);
    const onChange = () => setReduced(mql.matches);
    // Sync once in case the value changed between the initial state and the effect.
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/**
 * Animate a number from 0 → `target` over `durationMs` with an ease-out-cubic
 * curve via requestAnimationFrame. When `enabled` is false (e.g. reduced motion,
 * or the value shouldn't animate yet) it returns `target` immediately. The raw
 * (fractional) value is returned — round it at the call site for integer tiles.
 */
export function useCountUp(
  target: number,
  opts?: { enabled?: boolean | undefined; durationMs?: number | undefined }
): number {
  const enabled = opts?.enabled ?? true;
  const durationMs = opts?.durationMs ?? 700;
  const [value, setValue] = useState<number>(enabled ? 0 : target);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }
    // Start from 0 synchronously so there's no one-frame flash of the final value.
    setValue(0);
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(target * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, enabled, durationMs]);

  return value;
}
